import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
// import CyberSecurity from './components/CyberSecurity';

/* =========================
   Lazy Loaded Components
========================= */

// Layout
const Header = lazy(() => import('./components/Header'));
const Footer = lazy(() => import('./components/Footer'));

// Pages
const HomePage = lazy(() => import('./pages/HomePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const Partners = lazy(() => import('./pages/Partners'));
const CareerPage = lazy(() => import('./pages/CareerPage'));
const CommonProducts = lazy(() => import('./pages/CommonProducts'));
const CustomerStories = lazy(() => import('./components/CustomerStories'));
const CaseStudies = lazy(() => import('./components/CaseStudies'));

// Admin
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const AdminPanellogin = lazy(() => import('./pages/AdminPanellogin'));

// Services
const InfrastructureServices = lazy(() => import('./components/services/Infrastructure/InfrastructureServices'));
const ManagedServices = lazy(() => import('./components/services/Infrastructure/ManagedServices'));
const PremiumSupportServices = lazy(() => import('./components/services/Infrastructure/PremiumSupportServices'));
const DevelopmentService = lazy(() => import('./components/services/Infrastructure/DevelopmentService'));
const DisasterRecovery = lazy(() => import('./components/services/Infrastructure/DisasterRecovery'));
const Azure = lazy(() => import('./components/Azure'));
const Office365 = lazy(() => import('./components/Office365'));
const MicrosoftDynamics365 = lazy(() => import('./components/MicrosoftDynamics365'));
const MicrosoftAX = lazy(() => import('./components/MicrosoftAX'));
const BusinessIntelligence = lazy(() => import('./components/BusinessIntelligence'));
const SharePointDevelopment = lazy(() => import('./components/SharePointDevelopment'));
const ApplicationDevelopment = lazy(() => import('./components/ApplicationDevelopment'));
const WebsiteDevelopment = lazy(() => import('./components/WebsiteDevelopment'));
const MobileAppDevelopment = lazy(() => import('./components/MobileAppDevelopment'));
const CMMCComplianceServices = lazy(() => import('./components/services/CMMClicense'));
const GCCHighServices = lazy(() => import('./components/services/GCCHighServices'));
const Staffing = lazy(() => import('./components/Staffing'));
const AIService = lazy(() => import('./components/AI/AIMain'));
const AIandSecurity = lazy(() => import('./components/AIandSecurity'));

// Products
const Visitorlog = lazy(() => import('./components/Products/Visitorlog'));
const Neo = lazy(() => import('./components/Products/Neo'));
const Policyack = lazy(() => import('./components/Products/Policyack'));
const CmmcDashboard = lazy(() => import('./components/Products/CmmcDashboard'));
const NistDashboard = lazy(() => import('./components/Products/NistDashboard'));
const HipaaCompliance = lazy(() => import('./components/Products/HipaaCompliance'));
const TicketManagement = lazy(() => import('./components/Products/TicketManagement'));
const LeavesApp = lazy(() => import('./components/Products/LeavesApp'));
const Timesheet = lazy(() => import('./components/Products/Timesheet'));
const Travel = lazy(() => import('./components/Products/Travel'));
const ExpenseApproval = lazy(() => import('./components/Products/ExpenseApproval'));
const Blogs = lazy(() => import('./components/Blogs'));
const Privacy = lazy(() => import('./components/Privacy'));
const CCMCGroundwork = lazy(() => import('./components/CMMCGroundworkLanding'));
const Modern365 = lazy(() => import('./components/Modern365'));
const CyberSecurity = lazy(() => import('./components/CyberSecurity'));

/* =========================
   Loader Component
========================= */
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-screen">
      <p className="text-gray-500 text-lg">Loading...</p>
    </div>
  );
}

/* =========================
   Layout Wrapper
========================= */
function Layout({ children }) {
  const location = useLocation();
  const hideLayout = location.pathname.startsWith('/admin');

  return (
    <>
      {!hideLayout && <Header />}
      <main className="min-h-screen font-sans text-gray-900">
        {children}
      </main>
      {!hideLayout && <Footer />}
    </>
  );
}

/* =========================
   App Component
========================= */
function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // ✅ Fast auth check (no artificial delay)
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    setCheckingAuth(false);
  }, []);

  if (checkingAuth) {
    return <PageLoader />;
  }

  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Layout>
          <Routes>
            {/* Public */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/careers" element={<CareerPage />} />
            <Route path="/allproducts" element={<CommonProducts />} />
            <Route path="/customer-stories" element={<CustomerStories />} />
            <Route path="/cases" element={<CaseStudies />} />

            {/* Admin */}
            <Route
              path="/admin"
              element={
                isLoggedIn ? (
                  <AdminPanel
                    onLogout={() => {
                      localStorage.removeItem('token');
                      setIsLoggedIn(false);
                    }}
                  />
                ) : (
                  <AdminPanellogin
                    onLoginSuccess={(token) => {
                      localStorage.setItem('token', token);
                      setIsLoggedIn(true);
                    }}
                  />
                )
              }
            />

            {/* Services */}
            <Route path="/infrastructure-services" element={<InfrastructureServices />} />
            <Route path="/managed-services" element={<ManagedServices />} />
            <Route path="/premium-support-services" element={<PremiumSupportServices />} />
            <Route path="/automations-and-development" element={<DevelopmentService />} />
            <Route path="/disaster-and-recovery" element={<DisasterRecovery />} />
            <Route path="/azure" element={<Azure />} />
            <Route path="/office-365" element={<Office365 />} />
            <Route path="/dynamics-365" element={<MicrosoftDynamics365 />} />
            <Route path="/microsoft-ax" element={<MicrosoftAX />} />
            <Route path="/business-intelligence" element={<BusinessIntelligence />} />
            <Route path="/sharepoint-development" element={<SharePointDevelopment />} />
            <Route path="/application-development" element={<ApplicationDevelopment />} />
            <Route path="/website-development" element={<WebsiteDevelopment />} />
            <Route path="/ios-android-development" element={<MobileAppDevelopment />} />
            <Route path="/cmmc-compliance" element={<CMMCComplianceServices />} />
            <Route path="/gcch-compliance" element={<GCCHighServices />} />
            <Route path="/staffing" element={<Staffing />} />
            <Route path="/ai-services" element={<AIService />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cmmc-groundwork" element={<CCMCGroundwork />} />
            <Route path="/modern365" element={<Modern365 />} /> 
            <Route path="/cybersecurity" element={<CyberSecurity />} />
            <Route path="/ai-security" element={<AIandSecurity />} />

            {/* Products */}
            <Route path="/visitor-log" element={<Visitorlog />} />
            <Route path="/neo" element={<Neo />} />
            <Route path="/policyack" element={<Policyack />} />
            <Route path="/cmmc-dashboard" element={<CmmcDashboard />} />
            <Route path="/nist-dashboard" element={<NistDashboard />} />
            <Route path="/hipaa-compliance" element={<HipaaCompliance />} />
            <Route path="/ticket-management" element={<TicketManagement />} />
            <Route path="/leaves-app" element={<LeavesApp />} />
            <Route path="/timesheet" element={<Timesheet />} />
            <Route path="/travel" element={<Travel />} />
            <Route path="/expense-approval" element={<ExpenseApproval />} />

            {/* Fallback */}
            <Route path="*" element={<HomePage />} />
          </Routes>
        </Layout>
      </Suspense>
    </Router>
  );
}

export default App;
